import { useMemo, useState } from 'react';
import { episodeTags, seasons } from '@/mocks/russianMom';

export default function EpisodeSection() {
  const [seasonId, setSeasonId] = useState(seasons[0].id);
  const [tag, setTag] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [openEp, setOpenEp] = useState<string | null>(seasons[0].episodes[0].id);

  const activeSeason = seasons.find((s) => s.id === seasonId) ?? seasons[0];

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return activeSeason.episodes.filter((ep) => {
      const tagOk = tag === '全部' || ep.tags.includes(tag);
      const kwOk = kw === '' || ep.title.toLowerCase().includes(kw) || ep.summary.toLowerCase().includes(kw);
      return tagOk && kwOk;
    });
  }, [activeSeason, tag, keyword]);

  return (
    <section id="episodes" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <i className="ri-play-list-2-line" aria-hidden="true"></i>
              分集看点
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              全部剧集 · 共 20 集 中文字幕高清
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              按季切换、按标签筛选，或直接搜索关键词，快速定位到想看的某一集。点击任意一集可展开剧情看点。
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
            <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索集名或剧情节关键词"
              aria-label="搜索剧集"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none md:w-56"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword('')}
                aria-label="清空搜索"
                className="flex h-5 w-5 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200"
              >
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        {/* 季切换 */}
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
            {seasons.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSeasonId(s.id);
                  setOpenEp(s.episodes[0].id);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  seasonId === s.id
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-foreground-500">{activeSeason.subtitle}</p>
        </div>

        {/* 标签筛选 */}
        <div className="mt-4 flex flex-wrap gap-2">
          {episodeTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
                tag === t
                  ? 'border-accent-500 bg-accent-100 font-medium text-accent-900'
                  : 'border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300 hover:bg-accent-50'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* 列表 */}
        <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {list.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true"></i>
              <p className="text-sm text-foreground-600">没有找到匹配的剧集，试试换个关键词或标签。</p>
            </div>
          )}
          {list.map((ep, idx) => {
            const isOpen = openEp === ep.id;
            return (
              <div key={ep.id} className={idx !== 0 ? 'border-t border-background-200' : ''}>
                <button
                  type="button"
                  onClick={() => setOpenEp(isOpen ? null : ep.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-background-100 md:px-5"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      isOpen ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-800'
                    }`}
                  >
                    {String(ep.ep).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-heading text-base font-semibold text-foreground-950">{ep.title}</span>
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {ep.duration}
                      </span>
                      <span className="text-xs text-foreground-400">{ep.date}</span>
                    </div>
                    <div className="mt-1.5 hidden flex-wrap gap-1.5 sm:flex">
                      {ep.tags.map((tg) => (
                        <span key={tg} className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900">
                          {tg}
                        </span>
                      ))}
                    </div>
                  </div>
                  <i
                    className={`shrink-0 text-foreground-500 transition-transform ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}
                    aria-hidden="true"
                  ></i>
                </button>
                {isOpen && (
                  <div className="animate-fade-up border-t border-background-200 bg-background-100 px-4 py-4 md:px-5">
                    <p className="text-sm leading-7 text-foreground-700">{ep.summary}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-movie-2-line" aria-hidden="true"></i>
                        中文字幕 · 高清
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" aria-hidden="true"></i>
                        上线 {ep.date}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}